class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // Custom (Goodbands): 3-für-2- und Gratis-Versand-Hinweis im Cart-Popup
    this.updateGoodbandsMessages();

    if (this.header) this.header.reveal();
    this.open();
  }

  // ===== Custom (Goodbands), Stand 30.10.2026 =====
  // Das Popup zeigt nur Hinweise, die an der Kasse auch wirklich gelten.
  // Doku: snippets/goodbands-custom-code-doku.liquid
  updateGoodbandsMessages() {
    const messageEl = this.notification.querySelector('[data-free-shipping-message]');
    if (!messageEl) return;
    messageEl.innerHTML = '';

    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        // Je Hinweis eine eigene Zeile; Abstand dazwischen regelt .gb-cart-msg im Snippet
        messageEl.innerHTML = this.getGoodbandsMessages(cart)
          .map((text) => `<span class="gb-cart-msg">${window.freeShippingIcon || ''}${text}</span>`)
          .join('');
      })
      .catch((err) => {
        console.error('Fehler beim Holen der Cart-Daten:', err);
      });
  }

  getGoodbandsMessages(cart) {
    // Titel des automatischen Rabatts "3 FÜR 2" (Admin > Rabatte)
    const THREE_FOR_TWO_TITLE = /3\s*f(?:ü|ue)r\s*2/i;
    // Versandprofil: gratis ab 30,00 nach Rabatten (EUR in DE/AT, CHF in CH), in Cent
    const FREE_SHIPPING_MIN_TOTAL = 3000;
    // Rabatt "Versandkostenfrei ab 4 Artikeln" wird abgeschaltet (Entscheidung 30.10.2026),
    // deshalb 0 = aus. Falls er wieder aktiv wird: 4 eintragen (zählt dann alle Artikel,
    // greift aber nur ohne anderen Rabatt im Warenkorb, weil er nicht kombinierbar ist).
    const FREE_SHIPPING_MIN_ITEMS = 0;

    const items = cart.items || [];
    const messages = [];

    // 3-für-2 nur, wenn Shopify den Rabatt im Warenkorb tatsächlich angewendet hat.
    // Welche Produkte zählen, legt allein der Rabatt im Admin fest, nicht dieser Code.
    const threeForTwo = items.some((item) =>
      (item.line_level_discount_allocations || []).some((allocation) =>
        THREE_FOR_TWO_TITLE.test((allocation.discount_application || {}).title || '')
      )
    );
    if (threeForTwo) messages.push('3-für-2 freigeschaltet');

    const freeByTotal = cart.total_price >= FREE_SHIPPING_MIN_TOTAL;
    const freeByItems =
      FREE_SHIPPING_MIN_ITEMS > 0 &&
      cart.item_count >= FREE_SHIPPING_MIN_ITEMS &&
      cart.total_discount === 0;
    if (freeByTotal || freeByItems) messages.push('Gratis Versand freigeschaltet');

    return messages;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);